// Préstamos internos de la empresa (cuota mensual descontada en el rol).
// Nómina → Movimientos.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, HandCoins, Loader2, Plus, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { Campo, Modal, fieldCls, fecha, money, nomRpc, useDialogoEnLinea, type EmpleadoRow, type PrestamoRow, hoyISO } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

const vacio = { empleado_id: '', fecha: hoyISO(), monto: '', cuotas: '6', motivo: '' };

export function Prestamos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { isAdmin } = usePermissions();
  const [estado, setEstado] = useState('solicitado');
  const { data: rows = [], isFetching } = useQuery<PrestamoRow[]>({
    queryKey: ['nom-prestamos', estado],
    queryFn: () => nomRpc<PrestamoRow[]>('nom_prestamos', { _empleado_id: null, _estado: estado }),
  });
  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt-act'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: true }),
  });

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abonando, setAbonando] = useState<PrestamoRow | null>(null);
  const [montoAbono, setMontoAbono] = useState('');
  const [notaAbono, setNotaAbono] = useState('');

  const cuotaPreview = Number(form.monto) > 0 && Number(form.cuotas) > 0 ? Number(form.monto) / Number(form.cuotas) : null;

  const crear = useMutation({
    mutationFn: () => nomRpc('nom_prestamo_crear', { _data: { ...form, monto: Number(form.monto), cuotas: Number(form.cuotas), motivo: form.motivo || null } }),
    onSuccess: () => { toast.success('Préstamo registrado (pendiente de aprobación)'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-prestamos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const aprobar = useMutation({
    mutationFn: (id: string) => nomRpc('nom_prestamo_aprobar', { _id: id }),
    onSuccess: () => { toast.success('Préstamo aprobado (activo)'); void qc.invalidateQueries({ queryKey: ['nom-prestamos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const rechazar = useMutation({
    mutationFn: async (id: string) => {
      const v = await dlg.pedir('Motivo del rechazo', [{ key: 'motivo', label: 'Motivo', minimo: 0 }]);
      if (v === null) return Promise.reject(new Error('Cancelado'));
      return nomRpc('nom_prestamo_rechazar', { _id: id, _motivo: v.motivo || null });
    },
    onSuccess: () => { toast.success('Préstamo rechazado'); void qc.invalidateQueries({ queryKey: ['nom-prestamos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const abonar = useMutation({
    mutationFn: () => nomRpc('nom_prestamo_abonar', { _id: abonando!.id, _monto: Number(montoAbono), _nota: notaAbono || null }),
    onSuccess: () => { toast.success('Abono registrado'); setAbonando(null); void qc.invalidateQueries({ queryKey: ['nom-prestamos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => nomRpc('nom_prestamo_anular', { _id: id }),
    onSuccess: () => { toast.success('Préstamo anulado'); void qc.invalidateQueries({ queryKey: ['nom-prestamos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalSaldo = rows.reduce((s, p) => s + Number(p.saldo), 0);
  const totalMonto = rows.reduce((s, p) => s + Number(p.monto), 0);
  const totalCuota = rows.reduce((s, p) => s + Number(p.cuota_mensual), 0);

  const headersExp = ['N°', 'Código', 'Empleado', 'Fecha', 'Monto', 'Cuota', 'Saldo', 'Abonos', 'Estado'];
  const MONEYCOLS = [4, 5, 6];
  const filasExport: Celda[][] = [
    ...rows.map((p, i): Celda[] => [
      i + 1, p.codigo ?? '', p.razon_social, fecha(p.fecha),
      Number(p.monto), Number(p.cuota_mensual), Number(p.saldo), p.abonos.length, p.estado,
    ]),
    ['', '', '', 'TOTAL', totalMonto, totalCuota, totalSaldo, '', ''],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Préstamos internos</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`prestamos-internos-${fechaLocal()}`} titulo="Préstamos internos"
            headers={headersExp} filas={filasExport} colsDerecha={[0, 4, 5, 6]} disabled={rows.length === 0}
            fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
          />
          <Button onClick={() => { setForm(vacio); setAbierto(true); }}><Plus className="w-4 h-4 mr-1" /> Nuevo préstamo</Button>
        </div>
      </div>

      <select className={`${fieldCls} w-56`} value={estado} onChange={(e) => setEstado(e.target.value)} aria-label="Estado">
        <option value="solicitado">Pendientes de aprobación</option>
        <option value="activo">Activos</option>
        <option value="cancelado">Cancelados</option>
        <option value="anulado">Anulados</option>
        <option value="rechazado">Rechazados</option>
        <option value="todos">Todos</option>
      </select>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Préstamos ({rows.length}) — saldo por cobrar {money(totalSaldo)}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Fecha</th>
              <th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3 text-right">Cuota</th>
              <th className="py-2 pr-3 text-right">Saldo</th><th className="py-2 pr-3">Abonos</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin préstamos</td></tr>}
              {rows.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{p.codigo}</td>
                  <td className="py-2 pr-3">{p.razon_social}<div className="text-xs text-muted-foreground font-mono">{p.identificacion}</div></td>
                  <td className="py-2 pr-3 font-mono">{fecha(p.fecha)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(p.monto)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(p.cuota_mensual)} <span className="text-xs text-muted-foreground">×{p.cuotas}</span></td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(p.saldo)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.abonos.length}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${p.estado === 'activo' ? 'bg-pendiente-soft text-pendiente' : p.estado === 'cancelado' ? 'bg-completado-soft text-completado' : p.estado === 'solicitado' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300' : 'bg-muted text-muted-foreground'}`}>
                      {p.estado === 'solicitado' ? 'pendiente' : p.estado}
                    </span>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {p.estado === 'solicitado' && isAdmin() && (
                      <>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-completado hover:bg-completado-soft" title="Aprobar" onClick={async () => { if (await dlg.confirmar('¿Aprobar el préstamo?', { detalle: 'Quedará activo y se descontará en el rol.', aceptar: 'Aprobar' })) aprobar.mutate(p.id); }}><CheckCircle2 className="w-4 h-4" /></button>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Rechazar" onClick={() => rechazar.mutate(p.id)}><XCircle className="w-4 h-4" /></button>
                      </>
                    )}
                    {p.estado === 'activo' && (
                      <>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Registrar abono" onClick={() => { setAbonando(p); setMontoAbono(String(Math.min(Number(p.cuota_mensual), Number(p.saldo)))); setNotaAbono(''); }}><HandCoins className="w-4 h-4" /></button>
                        {p.abonos.length === 0 && (
                          <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Anular" onClick={async () => { if (await dlg.confirmar('¿Anular préstamo?', { peligro: true, aceptar: 'Anular' })) anular.mutate(p.id); }}><Ban className="w-4 h-4" /></button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Nuevo préstamo" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Empleado *">
              <select className={fieldCls} value={form.empleado_id} onChange={(e) => setForm({ ...form, empleado_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {empleados.map((e) => <option key={e.id} value={e.id}>{e.razon_social} ({e.identificacion})</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></Campo>
              <Campo label="Monto ($) *"><input type="number" min="0" step="0.01" className={fieldCls} value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} /></Campo>
              <Campo label="Cuotas mensuales *"><input type="number" min="1" className={fieldCls} value={form.cuotas} onChange={(e) => setForm({ ...form, cuotas: e.target.value })} /></Campo>
            </div>
            {cuotaPreview != null && <div className="text-sm">Cuota mensual: <span className="font-mono font-semibold">{money(cuotaPreview)}</span> (se descuenta en cada rol)</div>}
            <Campo label="Motivo"><input className={fieldCls} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} /></Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={crear.isPending} disabled={!form.empleado_id || !Number(form.monto) || !Number(form.cuotas)} onClick={() => crear.mutate()}>Crear préstamo</Button>
            </div>
          </div>
        </Modal>
      )}

      {abonando && (
        <Modal title={`Abonar a ${abonando.codigo ?? 'préstamo'}`} onClose={() => setAbonando(null)}>
          <div className="space-y-3">
            <div className="text-sm">{abonando.razon_social} — saldo <span className="font-mono font-semibold">{money(abonando.saldo)}</span></div>
            <Campo label="Monto del abono ($) *"><input type="number" min="0" step="0.01" className={fieldCls} value={montoAbono} onChange={(e) => setMontoAbono(e.target.value)} /></Campo>
            <Campo label="Nota"><input className={fieldCls} value={notaAbono} onChange={(e) => setNotaAbono(e.target.value)} placeholder="Abono en ventanilla…" /></Campo>
            {abonando.abonos.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Últimos abonos: {abonando.abonos.slice(0, 3).map((a) => `${fecha(a.fecha)} ${money(a.monto)}`).join(' · ')}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbonando(null)}>Cancelar</Button>
              <Button isLoading={abonar.isPending} disabled={!Number(montoAbono)} onClick={() => abonar.mutate()}>Registrar abono</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
